const page = '/p';
const admin = '/a';

const query = (params: Record<string, string | undefined>) => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) searchParams.set(key, value);
  }
  return searchParams.size > 0 ? '?' + searchParams.toString() : '';
};

export const paths = {
  landing:
    import.meta.env.MODE === 'production'
      ? 'https://www.synoptic.com'
      : '/auth/login',

  home: `${page}/home`,
  explore: `${page}/home/explore`,

  privacyPolicy: '/policy',
  termsOfUse: '/terms',

  login: '/auth/login',
  makeLogin: (from?: string, oauth?: string) =>
    `/auth/login${query({ from, oauth })}`,
  signup: '/auth/signup',
  makeSignup: (from?: string, oauth?: string) =>
    `/auth/signup${query({ from, oauth })}`,
  makeResetPassword: (from?: string, oauth?: string, username?: string) =>
    `/auth/reset-password${query({ from, oauth, username })}`,
  makeOauthCallback: (from?: string, code?: string) =>
    `/auth/oauth-callback${query({ from, code, oauth: 'true' })}`,

  makeUser: (name: string) => `/${name}`,
  makeFollowers: (name: string) => `/${name}/followers`,
  makeFollowing: (name: string) => `/${name}/following`,

  post: `${page}/posts/:id`,
  makePost: (postId: string) => `${page}/posts/${postId}`,

  tournaments: `${page}/tournaments`,
  activeTournaments: `${page}/tournaments/active`,
  makeActiveTournaments: (category: string) =>
    `${page}/tournaments/active?category=${category}`,
  closedTournaments: `${page}/tournaments/closed`,
  makeClosedTournaments: (category: string) =>
    `${page}/tournaments/closed?category=${category}`,
  makeTournament: (id: string) => `${page}/tournaments/${id}`,
  makeSubmissions: (id: string) => `${page}/tournaments/${id}/submissions`,
  makeQuestionnaire: (id: string) => `${page}/tournaments/${id}/questionnaire`,
  makeParticipants: (id: string) => `${page}/tournaments/${id}/participants`,
  makeRules: (id: string) => `${page}/tournaments/${id}/rules`,
  makeDiscussions: (id: string) => `${page}/tournaments/${id}/discussions`,

  marketplace: `${page}/marketplace`,
  streams: `${page}/marketplace/streams`,
  streamsFollowing: `${page}/marketplace/streams/following`,
  streamsCreated: `${page}/marketplace/streams/created`,
  makeStream: (id: string) => `${page}/streams/${id}`,
  makeStreamUnlock: (id: string) => `${page}/streams/${id}/unlock`,
  makeStreamDiscussions: (id: string) => `${page}/streams/${id}/discussions`,
  makeStreamApi: (id: string) => `${page}/streams/${id}/api`,
  makeStreamReaderApi: (id: string) => `${page}/streams/${id}/reader-api`,
  makeStreamRegionStats: (id: string) => `${page}/streams/${id}/region-stats`,
  makeStreamStats: (id: string) => `${page}/streams/${id}/stats`,
  makeStreamRoutes: (id: string) => `${page}/streams/${id}/routes`,
  makeStreamExport: (id: string) => `${page}/streams/${id}/enterprise-export`,
  makeStreamUsers: (id: string) => `${page}/streams/${id}/users`,

  customStreams: `${page}/marketplace/custom-streams`,
  customStreamsFollowing: `${page}/marketplace/custom-streams/following`,
  customStreamsCreated: `${page}/marketplace/custom-streams/created`,
  makeStreamTargets: (id: string) => `${page}/streams/${id}/targets`,

  streamPackages: `${page}/marketplace/stream-packages`,
  subscribedStreamPackages: `${page}/marketplace/stream-packages/subscribed`,
  createdStreamPackages: `${page}/marketplace/stream-packages/created`,
  makeStreamPackage: (id: string) => `${page}/stream-packages/${id}`,
  makeStreamPackageUnlock: (id: string) =>
    `${page}/stream-packages/${id}/unlock`,

  notifications: `${page}/notifications`,
  notificationInvites: `${page}/notifications/invites`,

  search: `${page}/search`,
  makeSearch: (q?: string, type?: string) =>
    `${page}/search${query({ q, t: type })}`,

  settings: `${page}/settings`,
  settingsPersonalDetails: `${page}/settings/personal-details`,
  settingsConnectedAccounts: `${page}/settings/connected-accounts`,
  settingsSecurity: `${page}/settings/security`,
  settingsPassword: `${page}/settings/security/password`,
  settingsPayouts: `${page}/settings/payouts`,
  settingsApiKeys: `${page}/settings/api-keys`,

  notificationsSettings: `${page}/settings/notifications/all`,
  notificationsSettingsEmail: `${page}/settings/notifications/email`,
  notificationsSettingsInApp: `${page}/settings/notifications/in-app`,
  notificationsSettingsPush: `${page}/settings/notifications/push`,

  learningCenter: `${page}/learning-center`,

  newsDeck: `${page}/news-deck`,
  terminal: `${page}/terminal`,
  terminalSettings: `${page}/terminal/settings`,
  terminalSettingsTradingButtons: `${page}/terminal/settings/trading-buttons`,
  terminalSettingsExchangeCredentials: `${page}/terminal/settings/exchange-credentials`,
  terminalSettingsTradingKeywords: `${page}/terminal/settings/trading-keywords`,
  terminalSettingsStreamListFilters: `${page}/terminal/settings/stream-list-filters`,

  tools: `${page}/tools`,
  toolsTwttrApi: `${page}/tools/twttr-api`,
  toolsTwttrApiPrices: `${page}/tools/twttr-api/prices`,
  toolsTwttrApiSubscriptions: `${page}/tools/twttr-api/subscriptions`,
  toolsKiyotakaChart: `${page}/tools/kiyotaka-chart`,
  toolsTradesChart: `${page}/tools/trades-chart`,
  toolsStreamPostsSearch: `${page}/tools/stream-posts-search`,
  toolsVox: `${page}/tools/vox`,
  toolsVoxText: `${page}/tools/vox/text`,
  toolsVoxUsers: `${page}/tools/vox/users`,
  toolsVoxNames: `${page}/tools/vox/names`,
  toolsVoxSettings: `${page}/tools/vox/settings`,
  toolsVoxHistory: `${page}/tools/vox/history`,
  enrichers: `${page}/enrichers`,
  makeEnricher: (enricherId: string) => `${page}/enrichers/${enricherId}`,

  xGraph: `/ct`,
  ctStats: `/ct-stats`,

  enterprise: `/enterprise`,
  enterpriseStreams: `/enterprise/streams`,
  enterpriseLogin: '/enterprise/login',
} as const;

export const adminPaths = {
  dashboard: `${admin}`,
  streamsStats: `${admin}/streams-stats`,
  polymatcherInferences: `${admin}/polymatcher-inferences`,
  translateFastInferences: `${admin}/translate-fast-inferences`,
  tournaments: `${admin}/tournaments`,
  makeTournament: (id: string) => `${admin}/tournaments/${id}`,
  organizations: `${admin}/organizations`,
  makeOrganization: (id: string) => `${admin}/organizations/${id}`,
  users: `${admin}/users`,
  makeUserSubscriptions: (userId: string, username?: string) =>
    `${admin}/users/${userId}/subscriptions${username ? `?username=${username}` : ''}`,
  vox: `${admin}/vox`,
  sources: `${admin}/sources`,
  makeSource: (sourceId: string) => `${admin}/sources/${sourceId}`,
  makeSourceRoutes: (sourceId: string) => `${admin}/sources/${sourceId}/routes`,
  makeSourceTags: (sourceId: string) => `${admin}/sources/${sourceId}/tags`,
  makeSourceEnrichments: (sourceId: string) =>
    `${admin}/sources/${sourceId}/enrichments`,
  makeSourceApiKeys: (sourceId: string) =>
    `${admin}/sources/${sourceId}/api-keys`,
  makeSourceHistory: (sourceId: string) =>
    `${admin}/sources/${sourceId}/history`,
  makeSourceDocs: (sourceId: string) => `${admin}/sources/${sourceId}/docs`,
} as const;
